import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BulletListText,
  BusinessChart,
  Calendar,
  CalendarUpcoming,
  CheckCircle,
  Heart,
  Inbox,
  Repeat,
  Search,
  Target,
  Trash2,
} from 'shared/assets/icons';
import { SideNavigationItem } from 'shared/components/ui/side-navigation-item';
import { useHistoryRoutesStack } from 'shared/contexts/history';
import { useFocusChildrenWithArrows } from 'shared/hooks/use-focus-children-with-arrows';
import { QueryParamOptions } from 'shared/lib/react-router-dom';
import { Paths } from 'shared/routes';
import {
  getPathByString,
  getPathVariablesByString,
} from 'shared/utils/get-path-by-string';

import { DetailPageLink } from './detail-page-link';
import * as Styled from './recent-visited-pages.style';

const pathLabelMap: Omit<
  Record<Paths, string>,
  | Paths.Login
  | Paths.Signup
  | Paths.ForgotPassword
  | Paths.ResetPassword
  | Paths.CompleteSignup
  | Paths.VerifyEmail
  | Paths.TagDetail
  | Paths.SmartListDetail
  | Paths.GoalDetail
  | Paths.GoalDetailRoadmap
  | Paths.GoalDetailProgress
  | Paths.GoalDetailTasks
  | Paths.LifeAreaDetail
  | Paths.LifeAreaDetailVision
  | Paths.LifeAreaDetailGoals
  | Paths.LifeAreaDetailTasks
  | Paths.TaskCreate
> = {
  [Paths.Home]: 'dialogs.search.recent-visited.pages.home.label',
  [Paths.Inbox]: 'dialogs.search.recent-visited.pages.inbox.label',
  [Paths.Upcoming]: 'dialogs.search.recent-visited.pages.upcoming.label',
  [Paths.AllTasks]: 'dialogs.search.recent-visited.pages.all-tasks.label',
  [Paths.Completed]: 'dialogs.search.recent-visited.pages.completed.label',
  [Paths.Goals]: 'dialogs.search.recent-visited.pages.goals.label',
  [Paths.GoalsRoadmap]:
    'dialogs.search.recent-visited.pages.goals-roadmap.label',
  [Paths.GoalsCompleted]:
    'dialogs.search.recent-visited.pages.goals-completed.label',
  [Paths.GoalsArchived]:
    'dialogs.search.recent-visited.pages.goals-archived.label',
  [Paths.GoalsThisYear]:
    'dialogs.search.recent-visited.pages.goals-this-year.label',
  [Paths.GoalsNextYear]:
    'dialogs.search.recent-visited.pages.goals-next-year.label',
  [Paths.GoalsUnplanned]:
    'dialogs.search.recent-visited.pages.goals-unplanned.label',
  [Paths.GoalsCalendar]:
    'dialogs.search.recent-visited.pages.goals-calendar.label',
  [Paths.GoalTemplates]:
    'dialogs.search.recent-visited.pages.goal-templates.label',
  [Paths.LifeAreas]: 'dialogs.search.recent-visited.pages.life-areas.label',
  [Paths.Habits]: 'dialogs.search.recent-visited.pages.habits.label',
  [Paths.HabitsCompleted]:
    'dialogs.search.recent-visited.pages.habits-completed.label',
  [Paths.Insights]: 'dialogs.search.recent-visited.pages.insights.label',
  [Paths.Search]: 'dialogs.search.recent-visited.pages.search.label',
  [Paths.Trash]: 'dialogs.search.recent-visited.pages.trash.label',
};

const pathIconMap: Omit<
  Record<Paths, React.FunctionComponent<React.SVGProps<SVGSVGElement>>>,
  | Paths.Login
  | Paths.Signup
  | Paths.ForgotPassword
  | Paths.ResetPassword
  | Paths.CompleteSignup
  | Paths.VerifyEmail
  | Paths.TagDetail
  | Paths.SmartListDetail
  | Paths.GoalDetail
  | Paths.GoalDetailRoadmap
  | Paths.GoalDetailProgress
  | Paths.GoalDetailTasks
  | Paths.LifeAreaDetail
  | Paths.LifeAreaDetailVision
  | Paths.LifeAreaDetailGoals
  | Paths.LifeAreaDetailTasks
  | Paths.TaskCreate
> = {
  [Paths.Home]: Calendar,
  [Paths.Inbox]: Inbox,
  [Paths.Upcoming]: CalendarUpcoming,
  [Paths.AllTasks]: BulletListText,
  [Paths.Completed]: CheckCircle,
  [Paths.Goals]: Target,
  [Paths.GoalsRoadmap]: Target,
  [Paths.GoalsCompleted]: Target,
  [Paths.GoalsArchived]: Target,
  [Paths.GoalsThisYear]: Target,
  [Paths.GoalsNextYear]: Target,
  [Paths.GoalsUnplanned]: Target,
  [Paths.GoalsCalendar]: Target,
  [Paths.GoalTemplates]: Target,
  [Paths.LifeAreas]: Heart,
  [Paths.Habits]: Repeat,
  [Paths.HabitsCompleted]: Repeat,
  [Paths.Insights]: BusinessChart,
  [Paths.Search]: Search,
  [Paths.Trash]: Trash2,
};

export type RecentVisitedPagesProps = {
  onItem?: () => void;
};

export const RecentVisitedPages: React.FC<RecentVisitedPagesProps> = ({
  onItem,
}) => {
  const listRef = useRef<HTMLUListElement>(null);

  const historyStack = useHistoryRoutesStack();
  const { t } = useTranslation();

  const stackedItems = useMemo(() => {
    const seenPaths = new Set();
    const seenSearches = new Set();
    return historyStack
      .toReversed()
      .filter(({ pathname, search }) =>
        pathname === Paths.Search
          ? !seenSearches.has(search.toLowerCase()) &&
            seenSearches.add(search.toLowerCase())
          : !seenPaths.has(pathname) && seenPaths.add(pathname),
      )
      .slice(0, 10)
      .map(({ pathname, search }) => ({
        link: `${pathname}${search}`,
        path: getPathByString(pathname),
        id: getPathVariablesByString(pathname)?.id ?? undefined,
      }));
  }, [historyStack]);

  useFocusChildrenWithArrows(listRef);

  return (
    <Styled.Container>
      <Styled.List ref={listRef}>
        {stackedItems.map(({ link, path, id }, index) => {
          const searchLabel =
            (path === Paths.Search &&
              new URLSearchParams(link.split('?')[1]).get(
                QueryParamOptions.Search,
              )) ||
            undefined;

          return (
            <Styled.ListItem key={`recent-${index}`}>
              {[
                Paths.TagDetail,
                Paths.GoalDetail,
                Paths.LifeAreaDetail,
                Paths.LifeAreaDetailGoals,
                Paths.LifeAreaDetailTasks,
              ].includes(path!) ? (
                <DetailPageLink link={link} id={id} onClick={onItem} />
              ) : (
                <SideNavigationItem
                  label={
                    searchLabel
                      ? `"${searchLabel}"`
                      : t(pathLabelMap[path as keyof typeof pathLabelMap])
                  }
                  icon={pathIconMap[path as keyof typeof pathLabelMap]}
                  onClick={onItem}
                  link={link}
                  activeSearchParamsToIgnore={[QueryParamOptions.Search]}
                />
              )}
            </Styled.ListItem>
          );
        })}
      </Styled.List>
    </Styled.Container>
  );
};
